import { useState, useEffect } from "react"

/**
 * Best practice device detection hook using CSS media queries (matchMedia)
 * This matches exactly what CSS uses for responsive breakpoints, making it the most reliable method.
 *
 * Uses matchMedia API which is the industry standard for responsive JavaScript detection.
 * This is more reliable than window.innerWidth because:
 * - It matches CSS breakpoints exactly
 * - It accounts for browser zoom and device pixel ratio
 * - It's the same API that CSS uses internally
 *
 * @param {number} [mobileBreakpoint=768] - The breakpoint for mobile (matches Tailwind's md: { max: "767px" })
 * @returns {Object} { isMobile, width, height } - Device detection information
 */
export default function useDeviceDetection(mobileBreakpoint = 768) {
  const isSSR = typeof window === "undefined"

  // Tailwind uses: md: { max: "767px" } so mobile is <= 767px, desktop is >= 768px
  // Media query: max-width: 767px means mobile
  const mediaQueryString = `(max-width: ${mobileBreakpoint - 1}px)`

  // Initial state
  const getInitialState = () => {
    if (isSSR) {
      return {
        isMobile: false,
        width: null,
        height: null
      }
    }

    // Use matchMedia for initial detection (most reliable)
    const mediaQuery = window.matchMedia(mediaQueryString)
    return {
      isMobile: mediaQuery.matches,
      width: window.innerWidth,
      height: window.innerHeight
    }
  }

  const [deviceInfo, setDeviceInfo] = useState(getInitialState)

  useEffect(() => {
    if (isSSR) return

    // Create media query matcher - this is the standard way to detect breakpoints
    const mediaQuery = window.matchMedia(mediaQueryString)

    // Update function
    const updateDeviceInfo = () => {
      setDeviceInfo({
        isMobile: mediaQuery.matches,
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    // Handle media query changes (fires when crossing breakpoint)
    const handleMediaQueryChange = (event) => {
      setDeviceInfo((prev) => ({
        ...prev,
        isMobile: event.matches
      }))
    }

    // Also update width/height on resize (for components that need exact dimensions)
    const handleResize = () => {
      setDeviceInfo((prev) => ({
        ...prev,
        width: window.innerWidth,
        height: window.innerHeight
      }))
    }

    // Use modern API (supported in all modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleMediaQueryChange)
    } else {
      // Fallback for very old browsers (IE11)
      mediaQuery.addListener(handleMediaQueryChange)
    }

    window.addEventListener("resize", handleResize)

    // Initial sync
    updateDeviceInfo()

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleMediaQueryChange)
      } else {
        mediaQuery.removeListener(handleMediaQueryChange)
      }
      window.removeEventListener("resize", handleResize)
    }
  }, [mediaQueryString, isSSR])

  return deviceInfo
}
